import { useCallback, useEffect, useRef, useState } from 'react';

import { sectionLabel } from './homepageContent';

/**
 * The film band — the second fold of the logged-out landing, directly under
 * the hero. One wide 16:9 card whose thumbnail is alive: a short, silent
 * teaser loops in it (muted autoplay — the only autoplay browsers allow), the
 * brass play ring sits over it, and pressing play starts the full narrated
 * film from the top, with sound and the browser's own controls. When the film
 * ends the teaser comes back with "Watch again" and the trial CTA.
 *
 * Two files, on purpose. The teaser (14 s, 720p, no audio, ~1.8 MB) is what
 * autoplays, and only while the card is on screen. The full film (~21 MB)
 * has preload="none" — not a byte of it loads until someone presses play.
 * Autoplaying the film itself would cost every visitor 21 MB on mobile data.
 *
 * No moving thumbnail under prefers-reduced-motion or data-saver: those
 * visitors get the still poster, and the film still plays on press.
 *
 * Both are cut from demo-video/ (Remotion): the film by `remotion render`,
 * the teaser by demo-video/scripts/teaser.sh (poster -> the film's eight
 * paper transitions -> poster, so the loop seam is invisible). Versioned
 * filenames so nginx's year-long immutable cache is busted by a re-render.
 * v7 film: the hook scene moves with the narration (folder, FIR, client, clock) and the USPs sit on taped paper tags (poster and scene 2). v6 film: opens with the two USPs (bring your own cases in one click; import documents from Google Drive), as a scene and on the poster. v5 film: the logo is the app's own lockup (column, plinth, square). v4: the poster poses the lawyer's question and shows the answer card
 * (also the film's first frame). The narration's words are burned into the
 * picture, so there is no captions track (it would show them twice).
 */

export const FILM_SRC = '/film/iadvocate-film-v7.mp4';
export const FILM_POSTER = '/film/iadvocate-film-v7-poster.jpg';
export const FILM_TEASER = '/film/iadvocate-teaser-v4.mp4';
/** Rendered length of the film (89.0 s). The chip beside the ring, and its spoken form. */
export const FILM_DURATION_LABEL = '1:29';
export const FILM_DURATION_SPOKEN = '1 minute 29 seconds';

/** The moving thumbnail is a courtesy, never a cost: off for reduced motion and data saver. */
export function teaserAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData =
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  return !reduce && !saveData;
}

interface FilmBandProps {
  onLogin: () => void;
  loading: boolean;
}

type Phase = 'idle' | 'playing' | 'ended';

export default function FilmBand({ onLogin, loading }: FilmBandProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const teaserRef = useRef<HTMLVideoElement>(null);
  const filmRef = useRef<HTMLVideoElement>(null);
  const onScreen = useRef(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [teaserOn] = useState(teaserAllowed);

  const runTeaser = useCallback((run: boolean) => {
    const t = teaserRef.current;
    if (!t) return;
    if (run) {
      t.muted = true; // must be set before play() for autoplay to be allowed
      void t.play()?.catch(() => {});
    } else {
      t.pause();
    }
  }, []);

  // Loop the teaser only while the card is on screen and the film is not playing.
  useEffect(() => {
    if (!teaserOn) return undefined;
    const card = cardRef.current;
    if (!card || typeof IntersectionObserver !== 'function') {
      onScreen.current = true;
      runTeaser(phase !== 'playing');
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen.current = entry.isIntersecting;
        runTeaser(entry.isIntersecting && phase !== 'playing');
      },
      { threshold: 0.25 },
    );
    io.observe(card);
    return () => io.disconnect();
  }, [teaserOn, phase, runTeaser]);

  const play = useCallback(() => {
    const film = filmRef.current;
    if (!film) return;
    runTeaser(false);
    setPhase('playing');
    film.currentTime = 0;
    // play() only rejects when the browser blocks it (never for a click) —
    // fall back to the thumbnail rather than leave an unhandled rejection.
    void film.play()?.catch(() => setPhase('idle'));
  }, [runTeaser]);

  const playing = phase === 'playing';

  return (
    <section
      id="film"
      aria-labelledby="film-heading"
      className="scroll-mt-16 px-5 sm:px-8 lg:px-14 py-14 sm:py-20 border-t border-brass-hairline"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h2
          id="film-heading"
          className="font-display font-normal text-3xl lg:text-[44px] text-parchment"
        >
          Criminal Case End to End Flow{' '}
          <em className="font-editorial italic text-brass">at a Glance</em>
        </h2>
        <span className="font-plex text-[11px] tracking-[0.3em] text-brass">
          {sectionLabel('film')}
        </span>
      </div>
      <p className="text-base leading-[1.7] text-chamber-muted-bright max-w-[560px] mt-4">
        From the FIR to the hearing, narrated, in {FILM_DURATION_SPOKEN}. Every
        claim cited, or flagged unverified.
      </p>

      <figure className="mt-8 sm:mt-12 mx-auto max-w-[1180px]">
        <div
          ref={cardRef}
          className="relative aspect-video overflow-hidden border border-brass-hairline-soft bg-chamber-ink shadow-[0_24px_60px_-20px_rgba(0,0,0,0.75)]"
        >
          {/* The full film: nothing loads until play is pressed. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- captions are burned into the picture (every narrated line is on screen); a <track> would show them twice */}
          <video
            ref={filmRef}
            src={FILM_SRC}
            className={`absolute inset-0 h-full w-full ${playing ? '' : 'invisible'}`}
            preload="none"
            playsInline
            controls={playing}
            onEnded={() => setPhase('ended')}
            onPause={(e) => {
              // A pause at the very end is the ended event's twin; a pause
              // mid-way keeps the native controls so the lawyer can resume.
              if (e.currentTarget.ended) setPhase('ended');
            }}
            aria-label={`iAdvocate film — one criminal matter from FIR to hearing, narrated, ${FILM_DURATION_SPOKEN}, captions in picture`}
          />

          {!playing && (
            <div className="absolute inset-0 flex flex-col items-center justify-end gap-3 sm:gap-5 pb-[5%] sm:pb-[7%]">
              {/* The living thumbnail (or the still poster when motion is off).
                  Bottom-centred controls sit in the poster's clear lower band. */}
              {teaserOn ? (
                <video
                  ref={teaserRef}
                  data-testid="film-teaser"
                  className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                  src={FILM_TEASER}
                  poster={FILM_POSTER}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              ) : (
                <img
                  data-testid="film-poster"
                  src={FILM_POSTER}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                />
              )}
              <button
                type="button"
                onClick={play}
                aria-label={
                  phase === 'ended'
                    ? 'Watch the film again'
                    : `Play the film, ${FILM_DURATION_SPOKEN}, sound on`
                }
                className="group relative flex items-center gap-3 sm:gap-4 cursor-pointer"
              >
                <span className="flex h-14 w-14 sm:h-[72px] sm:w-[72px] items-center justify-center rounded-full border-2 border-brass bg-chamber-ink/85 text-brass shadow-[0_12px_30px_-8px_rgba(0,0,0,0.7)] transition-all motion-safe:group-hover:-translate-y-0.5 group-hover:border-brass-hover group-hover:text-brass-hover">
                  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M8 5v14l11-7z" />
                  </svg>
                </span>
                {/* A parchment pill: the teaser moves under it, so the label
                    carries its own ground instead of relying on the frame. */}
                <span className="rounded-full bg-parchment/90 px-3 py-1.5 font-plex text-[11px] font-semibold tracking-[0.28em] text-chamber-ink">
                  {phase === 'ended' ? 'WATCH AGAIN' : `${FILM_DURATION_LABEL} · SOUND ON`}
                </span>
              </button>

              {phase === 'ended' && (
                <button
                  type="button"
                  onClick={onLogin}
                  disabled={loading}
                  aria-label="Start free trial — sign in with Google"
                  className="relative bg-brass text-chamber-ink px-5 py-2.5 sm:px-7 sm:py-3.5 text-[13px] sm:text-sm font-semibold tracking-[0.1em] hover:bg-brass-hover transition-colors disabled:opacity-50 cursor-pointer"
                >
                  START FREE TRIAL
                </button>
              )}
            </div>
          )}
        </div>
        <figcaption className="mt-4 font-plex text-[10.5px] font-medium tracking-[0.22em] text-chamber-muted-faint">
          FIR ANALYSIS · CASE THEORY · HEARING PREP · COURT WATCH
        </figcaption>
      </figure>
    </section>
  );
}
